import React from 'react';

import { AppIcon, type ColorToken } from '@backrs_ui/common-ui';

import {
  GUIDE_ASIDE_WIDTH,
  GUIDE_TEXT_MAX_WIDTH,
  GuideCallout,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import type {
  OnlineReputationSignalTone,
  OnlineReputationWhyItMatters,
} from '@backrs_ui/grow-ui/src/pages/OnlineReputationGuide/content/types';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

type WhyItMattersSectionProps = Readonly<{
  content: OnlineReputationWhyItMatters;
}>;

export const WhyItMattersSection: React.FC<WhyItMattersSectionProps> = ({ content }) => (
  <Box sx={layoutSx}>
    <Box sx={containerSx}>
      <Typography variant="text-md-regular" sx={introSx}>
        {content.intro}
      </Typography>

      <Box sx={gridSx}>
        {content.signalGroups.map((group) => (
          <Box key={group.id} sx={groupSx}>
            <Chip
              label={group.title}
              variant="badge"
              size="sm"
              type="default"
              sx={chipSx(group.tone)}
            />
            <Box sx={itemListSx}>
              {group.items.map((item) => (
                <Box key={item} sx={itemSx}>
                  <Box sx={itemIconSx}>
                    <AppIcon
                      iconName="ArrowRight"
                      diameter="16px"
                      color="Colors/Foreground/fg-brand-primary"
                    />
                  </Box>
                  <Typography variant="text-md-regular" sx={itemTextSx}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Typography variant="text-md-regular" sx={introSx}>
        {content.outro}
      </Typography>
    </Box>

    <Box sx={asideSx}>
      <GuideCallout
        variant="highlight"
        title={content.bottomLine.title}
        body={content.bottomLine.body}
      />
    </Box>
  </Box>
);

const TONE_COLORS: Record<
  OnlineReputationSignalTone,
  Readonly<{ background: ColorToken; border: ColorToken; text: ColorToken }>
> = {
  success: { background: 'Success/50', border: 'Success/200', text: 'Success/700' },
  orange: { background: 'Orange/50', border: 'Orange/200', text: 'Orange/700' },
};

const layoutSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-7xl'],
  '@container (min-width: 600px)': {
    flexDirection: 'row',
  },
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  flex: 1,
  minWidth: 0,
});

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 600px)': {
    gridTemplateColumns: '1fr 1fr',
  },
});

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

const chipSx =
  (tone: OnlineReputationSignalTone): SxProps<Theme> =>
  ({ backrs }) => {
    const { background, border, text } = TONE_COLORS[tone];

    return {
      border: `1px solid ${backrs.colors[border]}`,
      backgroundColor: backrs.colors[background],
      color: backrs.colors[text],
      cursor: 'default',
      '&:hover': {
        backgroundColor: backrs.colors[background],
      },
    };
  };

const itemListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const itemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

// Vertically centers the arrow against the first line of the item text.
const itemIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexShrink: 0,
  pt: backrs.spacing['spacing-xs'],
});

const itemTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const asideSx: SxProps<Theme> = () => ({
  width: '100%',
  flexShrink: 0,
  '@container (min-width: 600px)': {
    width: GUIDE_ASIDE_WIDTH,
  },
});
